import React from "react";
import { DialogLayout } from "../../../ui/layouts/DialogLayout";
import { Button } from "../../../ui/components/Button";
import { FeatherCheck, FeatherInfo } from "@subframe/core";

export type ApplyGLAsDefaultDocumentType = "bill" | "invoice";

export interface ApplyGLAsDefaultLineItem {
  lineItemIndex: number;
  description: string;
  accountId: string;
  accountName: string;
  accountCode?: string;
}

export interface ApplyGLAsDefaultModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentType: ApplyGLAsDefaultDocumentType;
  /** Vendor name on bills, customer name on invoices. */
  principalName?: string;
  lineItems: ApplyGLAsDefaultLineItem[];
  onNotNow: () => void;
  onSaveAsDefault: () => void;
  isLoading?: boolean;
}

const COPY: Record<
  ApplyGLAsDefaultDocumentType,
  { describe: string; applies: (principal: string) => string }
> = {
  bill: {
    describe:
      "We'll apply this GL to similar future bills based on the vendor and bill details. You can review and change it before approving.",
    applies: (principal) =>
      `Bills from ${principal} with similar bill details or line items.`,
  },
  invoice: {
    describe:
      "We'll apply this GL to similar future invoices based on the customer and invoice details. You can review and change it before approving.",
    applies: (principal) =>
      `Invoices from ${principal} with similar invoice details or line items.`,
  },
};

const formatGL = (item: ApplyGLAsDefaultLineItem): string =>
  item.accountCode ? `${item.accountCode} · ${item.accountName}` : item.accountName;

export const ApplyGLAsDefaultModal: React.FC<ApplyGLAsDefaultModalProps> = ({
  open,
  onOpenChange,
  documentType,
  principalName,
  lineItems,
  onNotNow,
  onSaveAsDefault,
  isLoading = false,
}) => {
  const copy = COPY[documentType];
  const principal = principalName?.trim() || (documentType === "bill" ? "this vendor" : "this customer");

  return (
    <DialogLayout open={open} onOpenChange={onOpenChange}>
      <div className="flex w-full max-w-[600px] flex-col items-start gap-6 bg-default-background px-6 py-6">
        <div className="flex w-full flex-col items-start gap-4">
          <span className="font-['Figtree'] text-[20px] font-[600] leading-[20px] text-default-font">
            Apply this GL automatically next time?
          </span>
          <span className="text-body font-body text-subtext-color">
            {copy.describe}
          </span>
        </div>

        <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-200 bg-neutral-50 px-5 py-4">
          <div className="flex w-full flex-col items-start gap-3">
            <div className="flex w-full items-center gap-4">
              <span className="text-caption-bold font-caption-bold text-subtext-color flex-1">
                Line Item
              </span>
              <span className="text-caption-bold font-caption-bold text-subtext-color text-right flex-1">
                GL
              </span>
            </div>
            {lineItems.map((item) => (
              <React.Fragment key={item.lineItemIndex}>
                <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-200" />
                <div className="flex w-full items-start gap-4">
                  <span className="text-body font-body text-default-font break-words min-w-0 flex-1">
                    {item.description || `Line Item ${item.lineItemIndex + 1}`}
                  </span>
                  <span className="text-body-bold font-body-bold text-default-font text-right break-words min-w-0 flex-1">
                    {formatGL(item)}
                  </span>
                </div>
              </React.Fragment>
            ))}
          </div>

          <div className="flex w-full flex-col items-start gap-1 rounded-lg bg-success-50 px-4 py-4">
            <span className="text-caption-bold font-caption-bold text-subtext-color">
              Applies to
            </span>
            <span className="text-body font-body text-default-font">
              {copy.applies(principal)}
            </span>
          </div>
        </div>

        <div className="flex w-full items-center justify-center gap-6">
          <Button
            variant="neutral-secondary"
            size="large"
            onClick={onNotNow}
            disabled={isLoading}
          >
            Not now
          </Button>
          <Button
            size="large"
            icon={<FeatherCheck />}
            onClick={onSaveAsDefault}
            loading={isLoading}
            disabled={isLoading}
          >
            Save as default suggestion
          </Button>
        </div>

        <div className="flex w-full items-start gap-2">
          <FeatherInfo className="text-body font-body text-warning-600 flex-none mt-0.5" />
          <span className="text-caption font-caption text-subtext-color">
            You can change the suggested GL before approval or remove this rule
            anytime.
          </span>
        </div>
      </div>
    </DialogLayout>
  );
};

export default ApplyGLAsDefaultModal;
